import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Plus, Trash2, ShieldCheck, ShieldX, Clock, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getVaultDocuments,
  createVaultDocument,
  withdrawVaultDocument,
  getPendingEmployeeDocuments,
  verifyEmployeeDocument
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/**
 * Two things that both involve documents, on one screen because the same person
 * does both: the company's own vault, and the queue of employee documents
 * waiting to be looked at.
 *
 * VERIFICATION IS A PERSON LOOKING. There is no government integration behind
 * the Verify button — the system checks that a PAN is shaped like a PAN and an
 * Aadhaar passes its check digit, which catches typos, not impostors. The
 * wording here says exactly that, because a green tick implying an identity
 * check would be worse than no check at all.
 */

const TABS = [
  { key: 'vault', label: 'Company vault', icon: FolderOpen },
  { key: 'verify', label: 'Employee documents', icon: ShieldCheck }
];

const DocumentVault = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const [tab, setTab] = useState('vault');

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Document Vault' }} />
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${
                tab === key ? 'bg-brand-700 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === 'vault' ? <VaultTab /> : <VerificationTab />}
      </div>
    </Layout>
  );
};

const VaultTab = () => {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['vaultDocuments'],
    queryFn: () => getVaultDocuments()
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['vaultDocuments'] });

  const save = useMutation({
    mutationFn: createVaultDocument,
    onSuccess: () => {
      refresh();
      setAdding(false);
      toast.success('Added to the vault');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not add it')
  });

  const withdraw = useMutation({
    mutationFn: withdrawVaultDocument,
    onSuccess: () => {
      refresh();
      toast.success('Withdrawn. It stays on file.');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not withdraw it')
  });

  const rows = data?.data ?? [];

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Callout tone="info" collapsible icon={<Info size={14} />} title="About company documents" className="min-w-0 flex-1 sm:max-w-2xl">
          Handbooks, forms and circulars you share with staff. The company&apos;s own KYB papers stay under Company.
        </Callout>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white"
        >
          <Plus size={15} /> Add document
        </button>
      </div>

      {isFetching ? (
        <Loader />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
          <FolderOpen size={28} className="mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">The vault is empty.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {rows.map((doc) => (
            <div key={doc.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <a
                    href={doc.file?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-gray-900 hover:text-brand-700 hover:underline"
                  >
                    {doc.title}
                  </a>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {[doc.category, audienceLabel(doc)].filter(Boolean).join(' · ')}
                  </div>
                  {doc.description && (
                    <p className="mt-1 text-sm text-gray-600">{doc.description}</p>
                  )}
                  {doc.expires_on && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-warning-fg">
                      <Clock size={11} /> Expires {shortDate(doc.expires_on)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={withdraw.isPending}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm('Withdraw this document? It stays on file and stops being circulated.')) {
                      withdraw.mutate(doc.id);
                    }
                  }}
                  className="shrink-0 rounded border border-danger-border px-2 py-1 text-xs text-danger-fg"
                  aria-label="Withdraw"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <AddDocumentModal
          busy={save.isPending}
          onClose={() => setAdding(false)}
          onSubmit={(values) => save.mutate(values)}
        />
      )}
    </>
  );
};

const VerificationTab = () => {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ['pendingEmployeeDocuments'],
    queryFn: () => getPendingEmployeeDocuments()
  });

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => verifyEmployeeDocument(id, payload),
    onSuccess: (_r, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pendingEmployeeDocuments'] });
      setRejecting(null);
      toast.success(variables.verified === false ? 'Marked not accepted' : 'Marked verified');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record that')
  });

  const rows = data?.data ?? [];

  return (
    <>
      <Callout tone="info" collapsible icon={<Info size={14} />} title="What verifying means" className="sm:max-w-2xl">
        Documents employees uploaded. Verifying means you opened it and it is what it says — nothing is checked against a government record, only the number&apos;s format.
      </Callout>

      {isFetching ? (
        <Loader />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
          <ShieldCheck size={28} className="mx-auto text-success-fg" />
          <p className="mt-2 text-sm text-gray-600">Nothing waiting.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Employee</Th>
                  <Th>Document</Th>
                  <Th>Number</Th>
                  <Th>Expires</Th>
                  <Th>Uploaded</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((doc) => (
                  <tr key={doc.id} className="border-t border-gray-100">
                    <Td className="font-medium text-gray-900">
                      {doc.employee?.name ?? '—'}
                    </Td>
                    <Td>
                      <a
                        href={doc.file?.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-700 hover:underline"
                      >
                        {doc.title ?? titleCase(doc.doc_type)}
                      </a>
                      <div className="text-xs text-gray-500">{titleCase(doc.doc_type)}</div>
                    </Td>
                    <Td className="text-xs text-gray-600">{doc.document_number ?? '—'}</Td>
                    <Td className="text-xs">{doc.expires_on ? shortDate(doc.expires_on) : '—'}</Td>
                    <Td className="text-xs text-gray-500">{shortDate(doc.uploaded_at)}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: doc.id, verified: true })}
                          className="flex items-center gap-1 rounded bg-brand-700 px-2.5 py-1 text-xs text-white disabled:opacity-50"
                        >
                          <ShieldCheck size={12} /> Verify
                        </button>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => setRejecting(doc)}
                          className="flex items-center gap-1 rounded border border-danger-border px-2.5 py-1 text-xs text-danger-fg disabled:opacity-50"
                        >
                          <ShieldX size={12} /> Reject
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejecting && (
        <RejectModal
          doc={rejecting}
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) =>
            decide.mutate({ id: rejecting.id, verified: false, reason })
          }
        />
      )}
    </>
  );
};

const RejectModal = ({ doc, busy, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal name={`Reject ${doc.title ?? titleCase(doc.doc_type)}`} onClose={onClose} width="460px">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          {doc.employee?.name ?? 'The employee'} is told, with this reason. A
          rejection they never hear about is a document that never gets
          replaced.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Illegible — please upload a clearer scan"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() => onSubmit(reason.trim())}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Reject'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const AddDocumentModal = ({ busy, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    url: '',
    filename: '',
    audience: 'ALL_EMPLOYEES',
    audience_verticals: '',
    expires_on: ''
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Modal name="Add to the vault" onClose={onClose} width="560px">
      <div className="space-y-3">
        <Field label="Title">
          <input
            value={form.title}
            onChange={set('title')}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Employee Handbook 2026"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <input
              value={form.category}
              onChange={set('category')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Handbook"
            />
          </Field>
          <Field label="Expires on (optional)">
            <input
              type="date"
              value={form.expires_on}
              onChange={set('expires_on')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label="Description">
          <input
            value={form.description}
            onChange={set('description')}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="File URL">
          <input
            value={form.url}
            onChange={set('url')}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Upload the file first, then paste its URL"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Who can see it">
            <select
              value={form.audience}
              onChange={set('audience')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="ALL_EMPLOYEES">Everyone</option>
              <option value="VERTICAL">Certain departments</option>
            </select>
          </Field>
          {form.audience === 'VERTICAL' && (
            <Field label="Departments">
              <input
                value={form.audience_verticals}
                onChange={set('audience_verticals')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Operations, Accounts"
              />
            </Field>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (!form.title.trim()) return toast.error('Give it a title');
              if (!form.url.trim()) return toast.error('A file URL is required');
              return onSubmit({
                title: form.title.trim(),
                category: form.category || null,
                description: form.description || null,
                file: { name: form.filename || form.title, url: form.url.trim() },
                audience: form.audience,
                audience_verticals: form.audience_verticals || null,
                expires_on: form.expires_on || null
              });
            }}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
    {children}
  </div>
);
const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

function audienceLabel(doc) {
  if (doc.audience === 'ALL_EMPLOYEES') return 'Everyone';
  const list = doc.audience_verticals ?? doc.audience_designations ?? doc.audience_user_ids;
  return list ? `${titleCase(doc.audience)}: ${list}` : `${titleCase(doc.audience)} (nobody)`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default DocumentVault;
